import { keyframes } from '@chakra-ui/react';
import {
    useEffect, useRef
} from 'react';

/**
 * Handle both the provided onClick handler and
 * the one for the ripple effect.
 */
export function useRippleEffectClick () {
    const ref = useRef<HTMLElement|null>(null);

    useEffect(() => {
        const current = ref.current;

        if (!current) {
            return;
        }

        current.addEventListener('click', handleRippleClick);

        return () => {
            current.removeEventListener('click', handleRippleClick);
        };
    }, [ref]);

    return ref;
}

/**
 * Add the element used in the ripple animation as a child of the event currentTarget.
 * The animation depends on the css styles in `rippleButtonStyles`.
 */
function handleRippleClick (this: HTMLElement, event: MouseEvent) {
    const buttonElement = event.currentTarget as HTMLElement;
    const pos = buttonElement.getBoundingClientRect();
    const circle = document.createElement('span');
    const diameter = Math.max(buttonElement.clientWidth, buttonElement.clientHeight);
    const radius = diameter / 2;

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - pos.left - radius}px`;
    circle.style.top = `${event.clientY - pos.top - radius}px`;
    circle.classList.add('ripple');

    const ripple = buttonElement.getElementsByClassName('ripple')?.[0];

    if (ripple) {
        ripple.remove();
    }
    buttonElement.appendChild(circle);
}

const ripple = keyframes`
  to {
    transform: scale(4);
    opacity: 0;
  }
`;

export const rippleButtonStyles = ({ rippleColor }: { rippleColor?: string }) => ({
    ['span.ripple']: {
        position: 'absolute',
        borderRadius: '50%',
        transform: 'scale(0)',
        animationName: `${ripple}`,
        animationDuration: '600ms',
        animationTimingFunction: 'linear',
        backgroundColor: rippleColor ? rippleColor : 'ripples.backgroundColor',
        pointerEvents: 'none'
    }
});
